import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { matchRoutes, renderRoutes, withRouter } from 'mage-react-router';
import cms from '@moka-fe/cms';
import classNames from 'classnames';
import deepEqual from 'deep-equal';
import { isBoolean, isFinite, omit, pick, toNumber } from 'lodash';
import { Alert, Button, Icon, Message, Modal, Upload } from 'sugar-design';

import UploadResumePromptModal from './UploadResumePromptModal';
import ExcelUploader from '@/components/candidateInfo/ExcelUploader';
import FormItem from '@/components/candidateInfo/FormItem';
import LoadingIndicator from '@/components/candidateInfo/LoadingIndicator';
import Breadcrumb from '@/components/common/Breadcrumb';
import Dialog from '@/components/common/Dialog';
import FormNav from '@/components/job/jobApply/FormNav';
import JobHeader from '@/components/job/jobApply/JobHeader';
import PcPolicyChecker from '@/components/privacy/PcPolicyChecker';

import { PRIVACY_SCOPE_FIELD_NEW } from '@/components/privacy/constants';
import { getPrivacySettingByType } from '@/components/privacy/service';
import {
  ACCEPTED_RESUME_MIMETYPE,
  APPLICATION_ATTACHMENT_TYPE,
  ATTACHMENT_ACCEPTABLE_TYPES,
  MAX_CANDIDATE_ATTACHMENT_SIZE,
  RESUME_ACCEPTABLE_NOTICE,
} from '@/constants/common';
import {
  aiCheckSimilarResult,
  batchUploadCandidates,
  checkDuplication,
  cleanTempResume,
  fetchExcelApplicationStats,
  getAiCheckDupId,
  uploadCandidate,
  uploadFile,
  uploadResume,
} from '@/redux/actions/applications';
import { fetchJob, fetchJobList } from '@/redux/actions/jobs';
import { getMandatoryField } from '@/redux/actions/org';
import { errorToast } from '@/redux/actions/toasts';
import { ERROR_CODES } from '@/tools/mokaRequest/errorCode';
import mokaRequest from '@/tools/mokaRequest';
import {
  downloadByUrl,
  emailValidator,
  filterDisabledFile,
  getReadableFileSize,
  mobilePhoneValidator,
  nameValidator,
  parsePhone,
  validateCertificate,
} from '@/utils/common';
import { getI18n } from '@/utils/i18n';

import styles from './index.cm.styl';

const i18n = getI18n();

const CATEGORIES = {
  basicInfo: ['email', 'name', 'phone', 'headhunterRecommendComment'],
  uploadInfo: ['resume', 'attachments'],
};

const CATEGORIES_TITLE = {
  uploadInfo: i18n.t`上传`,
  basicInfo: i18n.t`候选人信息`,
};

const RESUME_ACCEPTABLE_FILE_TYPES = [
  'pdf',
  'doc',
  'docx',
  'eml',
  'jpg',
  'jpeg',
  'png',
  'mht',
  'ppt',
  'pptx',
  'txt',
  'md',
  'wps',
];

const PLAIN_INPUT = [
  'name',
  'email',
  'phone',
  'citizenId',
  'salary',
  'aimSalary',
  'headhunterRecommendComment',
];

const getInitialState = () => ({
  formData: {
    resume: {},
    name: '',
    email: '',
    phone: '', // 传给后端的phone应该是不包括区号的
    salary: '',
    aimSalary: '',
    headhunterRecommendComment: '',
  },
  formError: {
    resume: '',
    email: '',
    phone: '',
    attachments: '',
    headhunterRecommendComment: '',
  },
  fileList: [],
  activeTabId: 'main',
  excelRows: [], // 解析出的候选人信息
  excelErrorMessage: '', // 错误文字
  excelUrl: '', // 包含错误内容的Excel文件地址，供修改用
  excelKey: '', // 成功处理后的OSS的Key
  excelName: '', // Excel原始文件名
  excelSubmitting: false, // 正在创建candidates
  submitting: false,
  isResumeUploaded: false, // 新建／修改时是否上传了简历
  showHint: false, // 是否显示核对候选人信息的提示
  showCheckResultDialog: false, // 是否展示简历自查结果dialog
  inProtect: false, // 简历自查的结果，是否在保护期内
  dropZoneActive: false, // 拖拽上传区域是否active
  status: '', // excel上传状态
  percent: 0, // excel上传进度（进度条）
  surplus: 0, // excel上传进度（秒数）
  mandatoryFields: [], // 候选人二次确认必填字段
  isPrivacyAgreed: false, // 是否同意隐私协议
  controlDuplicateApplications: false, // 是否被管控重复申请
  duplicateWhenUpload: false, // 候选人在系统中是否有重复
  isConfirmResumeAuthorization: false, // 是否二次确认
  isConfirmResumeModalOpen: false, // 是否展示二次确认弹窗
});

const mapStateToProps = (state) => {
  const applicationEntities = state.applications?.entities ?? {};
  Object.keys(applicationEntities).forEach((key) => {
    if (applicationEntities[key].fullPhone) {
      applicationEntities[key].phone = applicationEntities[key].fullPhone;
    }
  });
  return {
    orgEntities: state.org.entities,
    jobEntities: state.jobs.entities,
    applicationEntities,
    tempResume: state.applications.tempResume,
  };
};

const mapDispatchToProps = {
  uploadResume,
  cleanTempResume,
  uploadCandidate,
  batchUploadCandidates,
  fetchExcelApplicationStats,
  fetchJobList,
  fetchJob,
  errorToast,
  checkDuplication,
  getAiCheckDupId,
  aiCheckSimilarResult,
};

@withRouter
@connect(mapStateToProps, mapDispatchToProps)
class CandidateInfo extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ...getInitialState(),
      showPrivacy: false, // 是否显示隐私协议
      policyList: [], // 隐私协议列表
    };
    this.dragTargets = [];
  }

  componentDidMount() {
    const { orgId, jobId } = this.props.match.params;
    this.getPrivacySetting();
    const contractId = this.getContractId();

    if (!this.checkContractId(contractId)) {
      return;
    }

    this.props.fetchJob(orgId, jobId, contractId);

    // 如果是更新，不需要验证是否开启二次确认功能
    getMandatoryField(orgId, this.isRpoRecommend(), jobId).then((mandatoryFields) =>
      this.setState({ mandatoryFields })
    );
  }

  // 这里兼容旧路由或者手动输入的路由，如果contractId不是数字，就跳转到职位列表
  checkContractId = (contractId) => {
    const { orgId } = this.props.match.params;

    if (!isFinite(toNumber(contractId))) {
      this.props.history.replace(`/headhunters/${orgId}/org/jobs`);
      return false;
    }
    return true;
  };

  getPrivacySetting = () => {
    const {
      match: {
        params: { orgId },
      },
    } = this.props;
    getPrivacySettingByType(orgId, PRIVACY_SCOPE_FIELD_NEW.HEADHUNTERS_RECOMMEND_CANDIDATES).then(
      (res) => {
        if (res.length > 0) {
          this.setState({
            showPrivacy: true,
            policyList: res,
          });
        }
      }
    );
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.resumeOnUpload(nextProps.tempResume);
  }

  componentDidUpdate(preProps) {
    const { orgId, jobId } = this.props.match.params;
    // 投递成功是个子路由，判断一下是否路由变化，重新获取数据
    if (this.props.location.pathname !== preProps.location.pathname) {
      getMandatoryField(orgId, this.isRpoRecommend(), jobId).then((mandatoryFields) =>
        this.setState({ mandatoryFields })
      );
    }
  }

  componentWillUnmount() {
    this.props.cleanTempResume();
  }

  getCurrentJob = () => {
    const {
      match: {
        params: { jobId },
      },
      jobEntities,
    } = this.props;

    return jobEntities[jobId] || {};
  };

  getContractId = () => {
    const { contractId } = this.props.match.params;

    return contractId;
  };

  // 是否为 rpo 推荐
  isRpoRecommend = () => this.props.location?.query?.headhunterType === 'rpo';

  // 是否展示 rpo 相关的内容（tab、上传表单等等）
  isShowRpoContent = () => {
    const currentJob = this.getCurrentJob();

    return !!currentJob.rpoEnabled && this.isRpoRecommend();
  };

  onCloseCheckResultDialog = () => this.setState({ showCheckResultDialog: false });

  resumeOnUpload(tempResume = {}) {
    if (tempResume.loading || deepEqual(this.props.tempResume, tempResume)) {
      return;
    }

    const { formData } = this.state;
    // 解析出姓名或手机或邮箱时显示核对提示,且要覆盖已填写的值
    // 提示一旦显示，不消失
    let resumeFields = {};
    if (tempResume.name && !formData.name) {
      resumeFields.name = tempResume.name;
    }
    if (tempResume.email && !formData.email) {
      resumeFields.email = tempResume.email;
    }
    if (tempResume.fullPhone && !formData.phone) {
      resumeFields.phone = resumeFields.fullPhone = tempResume.fullPhone;
    }
    const showHint =
      this.state.showHint || !!(tempResume.name || tempResume.phone || tempResume.email);

    this.setState(
      {
        formData: {
          ...formData,
          ...resumeFields,
          resume: tempResume,
        },
        isResumeUploaded: true,
        showHint,
      },
      () => {
        if (JSON.stringify(tempResume) !== '{}' && tempResume) {
          this.validateForm();
        }
      }
    );
  }

  onDelete = (attachment) => {
    const { fileList } = this.state;
    this.setState({
      fileList: fileList.filter(
        (file) =>
          attachment.uid !== file.uid || attachment.id !== file.id || attachment.key !== file.key
      ),
    });
  };

  onUploadChange = ([file]) => {
    this.createFormErrorMsg('resume', '');
    const {
      orgEntities,
      match: {
        params: { orgId },
      },
    } = this.props;
    const currentOrg = orgEntities[orgId] || {};

    const { disabledUploadFileTypes = [] } = currentOrg;
    const acceptableReg = new RegExp(
      `\.(${[
        ...RESUME_ACCEPTABLE_FILE_TYPES,
        ...(!disabledUploadFileTypes.includes('html/htm') ? ['html', 'htm'] : []),
        ...(!disabledUploadFileTypes.includes('rtf') ? ['rtf'] : []),
      ].join('|')})$`
    );
    if (!file?.name?.match(acceptableReg)) {
      return this.createFormErrorMsg('resume', i18n.t`仅${RESUME_ACCEPTABLE_NOTICE}`);
    }

    this.props.uploadResume({ file, orgId });
  };

  onAttachmentUploadChange = (uploadingFileList) => {
    const { fileList } = this.state;
    const totalSize = uploadingFileList.reduce((result, { size }) => result + size, 0);
    if (totalSize > MAX_CANDIDATE_ATTACHMENT_SIZE) {
      const reason = i18n.t`上传文件失败：单次上传文件的总容量请小于50MB`;
      this.props.errorToast(reason);
      this.createFormErrorMsg('attachments', reason);
      return;
    }
    uploadingFileList.forEach((file) => (file.status = Upload.fileStatus.uploading));
    fileList.push(...uploadingFileList);
    this.setState({ fileList });
    uploadingFileList.forEach((file) => this.uploadAttachment(file));
  };

  uploadAttachment = (file) => {
    const { fileList } = this.state;
    const { orgId } = this.props.match.params;
    this.createFormErrorMsg('attachments', null);
    uploadFile(
      {
        file,
        orgId: orgId,
        onProgress: ({ percent }) => {
          const fileIndex = fileList.findIndex(({ uid }) => uid === file.uid);
          if (fileIndex === -1) {
            return;
          }
          file.percent = percent;
          fileList[fileIndex] = file;
          this.setState({ fileList });
        },
      },
      (error, result) => {
        if (error) {
          this.onDelete(file);
          this.createFormErrorMsg('attachments', error);
          return Message.sendMessage({ content: i18n.t`上传附件失败：` + error, type: 'error' });
        }
        const fileIndex = fileList.findIndex(({ uid }) => uid === file.uid);
        if (fileIndex === -1) {
          return;
        }
        file.key = result.key;
        file.status = error ? Upload.fileStatus.error : Upload.fileStatus.success;
        delete file.percent;
        fileList[fileIndex] = file;
        this.setState({ fileList });
      }
    );
  };

  validator(type, value) {
    switch (type) {
      case 'name':
        return nameValidator(value);
      case 'email':
        return emailValidator(value);
      case 'phone':
        return mobilePhoneValidator(value).isValid;
      case 'citizenId':
        return validateCertificate(value, this.state.formData.certificateType);
      default:
        return true;
    }
  }

  getValidateError(type, isNull, value) {
    const error = {
      isNull: {
        attachments: i18n.t`附件不能为空`,
        name: i18n.t`名字不能为空`,
        phone: i18n.t`电话不能为空`,
        email: i18n.t`邮箱不能为空`,
        headhunterRecommendComment: i18n.t`推荐评语不能为空`,
        salary: i18n.t`当前薪资不能为空`,
        aimSalary: i18n.t`期望薪资不能为空`,
        citizenId: i18n.t`身份证号不能为空`,
      },
      inValid: {
        name: i18n.t`请正确填写候选人姓名（二十字以内）`,
        phone: mobilePhoneValidator(value).message,
        email: i18n.t`邮箱格式有误`,
        citizenId: i18n.t`身份证格式有误`,
      },
    };
    const errType = isNull ? 'isNull' : 'inValid';
    return error[errType][type];
  }

  /**
   * 滚动到node可见的位置
   * @param {ElementNode} node 要可见的node
   * @param {number} offsetTop 距离顶部多少距离
   */
  scrollIntoView(node, offsetTop = 0) {
    const scrollTop = document.body.scrollTop;
    const { top } = node.getBoundingClientRect();
    // 这里使用，不这直接设置scrollTop是为了fix 这里直接设置scrollTop不tigger scroll事件
    window.scrollTo(0, scrollTop + top - offsetTop);
  }

  /**
   * 检查表单数据是否都合法
   * @param {boolean} isSubmit 是否是点击提交按钮的检查
   */
  validateForm = (isSubmit = false) => {
    const { formData, fileList } = this.state;
    let isValid = true;
    const newFormError = {};

    const { tempResume } = this.props;
    const resumeExists = !!tempResume.resumeName;

    if (!resumeExists) {
      newFormError.resume = i18n.t`必填`;
      isValid = false;
    }

    if (
      this.getIsRequire('attachments') &&
      this.getIsNotNull('attachments') &&
      fileList.length === 0
    ) {
      newFormError.attachments = i18n.t`必填`;
      isValid = false;
    }

    PLAIN_INPUT.forEach((type) => {
      // isRequired 为 false 时字段不显示，也没必要校验了
      // Jira：https://jira.mokahr.com/browse/MOKA-19999
      const isRequired = this.getIsRequire(type);
      if (!isRequired) {
        return;
      }

      const isNotNull = this.getIsNotNull(type);
      const value = formData[type];
      if ((value && !this.validator(type, value)) || (isNotNull && !value)) {
        isValid = false;
        newFormError[type] = this.getValidateError(type, !value, value);
      }
    });

    this.setState({ formError: newFormError }, () => {
      if (isSubmit === true) {
        const errorNode = document.querySelector('.form-item__error-msg');
        if (errorNode && errorNode.parentNode && errorNode.parentNode.parentNode) {
          this.scrollIntoView(errorNode.parentNode.parentNode, 80);
        }
      }
    });
    return isValid;
  };

  // 猎头自查前的表单验证，条件是需要填写 姓名+手机+邮箱
  validateFormForSelfCheck() {
    const { formData } = this.state;
    if (!formData.name || !formData.email || !formData.phone) {
      return false;
    }
    return true;
  }

  createFormOnChangeHandler = (name) => {
    return (firstValue, secondValue) => {
      const value = firstValue?.target ? firstValue.target.value : firstValue;
      const newFields = {
        [name]: value,
      };
      if (name === 'phone') {
        newFields.fullPhone = value;
        const [callingCode] = parsePhone(value);
        newFields.countryCallingCode = callingCode;
      }
      if (name === 'citizenId') {
        newFields.certificateType = secondValue;
      }
      this.setState({
        formData: {
          ...this.state.formData,
          ...newFields,
        },
        formError: {
          ...this.state.formError,
          [name]: '',
        },
      });
    };
  };

  createFormErrorMsg = (name, errorMsg) =>
    this.setState({
      formError: {
        ...this.state.formError,
        [name]: errorMsg,
      },
    });

  formOnSubmitFun = () => {
    const {
      match: {
        params: { orgId, jobId },
      },
      location: { pathname, query },
      orgEntities,
      tempResume,
    } = this.props;

    const { formData, fileList, isResumeUploaded, isConfirmResumeAuthorization } = this.state;
    const currentOrg = orgEntities[orgId] || {};
    // 猎头上传附件添加type
    const attachmentType = APPLICATION_ATTACHMENT_TYPE.CANDIDATE_UPLOAD_ATTACHMENT;
    const attachments = fileList.map((attachment) => ({
      ...omit(attachment, ['base64']),
      name: attachment.name,
      size: attachment.size,
      type: attachmentType,
    }));

    if (this.validateForm(true)) {
      this.setState({ submitting: true });
      const callback = (error) => {
        const resultUrl = `${pathname}/result?headhunterType=${query?.headhunterType ?? ''}`;
        if (!error) {
          this.setState(getInitialState());
          this.props.cleanTempResume();
          this.props.history.push(resultUrl);
          this.props.fetchJobList(orgId);
        } else if (
          [
            ERROR_CODES[700024],
            ERROR_CODES[701012],
            ERROR_CODES[701026],
            ERROR_CODES[701027],
          ].includes(error)
        ) {
          this.setState(getInitialState());
          this.props.cleanTempResume();
          this.props.history.push(resultUrl);
        } else {
          this.setState({ submitting: false });
        }
      };
      // 简历字段应该覆盖原有字段，表单填写字段优先级最高
      const {
        name,
        email,
        phone,
        countryCallingCode,
        fullPhone,
        salary,
        aimSalary,
        headhunterRecommendComment,
        certificateType,
        citizenId,
      } = formData;
      const params = {
        jobId: jobId,
        orgId: currentOrg.id,
        rpo: this.isRpoRecommend(),
        application: Object.assign(
          {},
          { ...formData, ...tempResume, attachments, stageId: null },
          {
            name,
            email,
            ...(countryCallingCode && { countryCallingCode }),
            phone,
            fullPhone,
            salary,
            aimSalary,
            headhunterRecommendComment,
            certificateType,
            citizenId,
          }
        ),
        isResumeUploaded,
        contractId: this.getContractId(),
        isConfirmResumeAuthorization,
        callback,
      };

      // 传给后端的phone应该是不包括区号的
      params.application.phone = params.application.phone?.replace(/(\+\d{1,4}\s)?/, '') ?? '';

      this.props.uploadCandidate(params);
    }
  };

  formOnSubmit = () => {
    const {
      match: {
        params: { orgId },
      },
      orgEntities,
    } = this.props;

    const { showPrivacy, isPrivacyAgreed, fileList } = this.state;
    const currentOrg = orgEntities[orgId] || {};

    const uploadingAttachments = fileList.filter((value) => !value.key);
    if (uploadingAttachments.length > 0) {
      this.setState({ isShowWaitModal: true });
      return;
    }
    const hasUploading = fileList.some((a) => !a.key);
    if (hasUploading) {
      return this.createFormErrorMsg('attachments', i18n.t`附件未上传完成`);
    }

    if (showPrivacy && !isPrivacyAgreed) {
      return Message.sendMessage({
        type: 'error',
        content: i18n.t`请阅读并同意隐私政策`,
      });
    }

    if (currentOrg.uploadResumePromptEnabled) {
      this.setState({ isConfirmResumeModalOpen: true });
    } else {
      this.formOnSubmitFun();
    }
  };

  onCheck = () => {
    const {
      match: {
        params: { orgId, jobId },
      },
      orgEntities,
      tempResume,
    } = this.props;
    const { formData, checking, fileList } = this.state;
    const currentOrg = orgEntities[orgId] || {};

    if (checking) {
      this.props.errorToast(i18n.t`正在检查，请稍等`);
      return;
    }

    const uploadingAttachments = fileList.filter((value) => !value.key);
    if (uploadingAttachments.length > 0) {
      this.setState({ isShowWaitModal: true });
      return;
    }

    // 猎头上传附件添加type
    const attachmentType = APPLICATION_ATTACHMENT_TYPE.CANDIDATE_UPLOAD_ATTACHMENT;
    const attachments = fileList.map((attachment) => ({
      ...omit(attachment, ['base64']),
      name: attachment.name,
      size: attachment.size,
      type: attachmentType,
    }));
    const {
      name,
      email,
      phone,
      countryCallingCode,
      fullPhone,
      salary,
      aimSalary,
      headhunterRecommendComment,
      certificateType,
      citizenId,
    } = formData;

    const checkApplication = Object.assign(
      {},
      { ...formData, ...tempResume, attachments, stageId: null },
      {
        name,
        email,
        ...(countryCallingCode && { countryCallingCode }),
        phone,
        fullPhone,
        salary,
        aimSalary,
        headhunterRecommendComment,
        certificateType,
        citizenId,
      }
    );

    if (this.validateFormForSelfCheck()) {
      this.setState({ checking: true, selfCheckError: '' });
      this.props.checkDuplication({
        orgId: currentOrg.id,
        jobId,
        contractId: this.getContractId(),
        application: {
          ...pick(formData, ['name', 'email', 'phone', 'citizenId', 'certificateType']),
          ...pick(formData.resume, [
            'birthYear',
            'gender',
            'location',
            'experienceInfo',
            'practiceInfo',
            'educationInfo',
          ]),
        },
        isRpo: this.isRpoRecommend(),
        callback: (
          error,
          {
            inProtect,
            controlDuplicateApplications,
            duplicateWhenUpload,
            isOpenAiCheckDup,
            aiCheckDupTime,
          } = {}
        ) => {
          // inProtect || controlDuplicateApplications  true 直接结束
          // 否则调用获取ai查重id接口 如果没有id 则使用老逻辑中的 duplicateWhenUpload
          // 否则 使用ai查重id 轮训查重接口
          // 根据结果展示查重
          if (error) {
            this.setState({ checking: false });
            return;
          }
          if (inProtect || controlDuplicateApplications || !isOpenAiCheckDup) {
            this.setState({
              checking: false,
              showCheckResultDialog: true,
              inProtect,
              controlDuplicateApplications,
              duplicateWhenUpload,
            });
            return;
          }

          if (isOpenAiCheckDup) {
            this.props.getAiCheckDupId({
              orgId: currentOrg.id,
              checkApplication,
              isRpo: this.isRpoRecommend(),
              jobId,
              // eslint-disable-next-line no-shadow
              callback: (error, { queryAiResultId }) => {
                if (error) {
                  this.setState({ checking: false });
                  return;
                }
                if (queryAiResultId) {
                  // ai查重需要轮训查结果
                  let timer = null;
                  let doing = false;
                  let isTimeout = false;
                  const timeoutTimer = setTimeout(() => {
                    isTimeout = true;
                  }, (Number(aiCheckDupTime) || 30) * 1000);
                  const getResult = () => {
                    if (doing) {
                      return;
                    }
                    doing = true;
                    this.props.aiCheckSimilarResult({
                      orgId: currentOrg.id,
                      queryAiResultId,
                      // eslint-disable-next-line no-shadow
                      callback: (error, { duplication }) => {
                        if (error) {
                          clearTimeout(timeoutTimer);
                          clearInterval(timer);
                          this.setState({ checking: false });
                          return;
                        }
                        if (isBoolean(duplication)) {
                          clearTimeout(timeoutTimer);
                          clearInterval(timer);
                          this.setState({
                            checking: false,
                            showCheckResultDialog: true,
                            inProtect,
                            controlDuplicateApplications,
                            duplicateWhenUpload: duplication,
                          });
                          return;
                        }
                        if (isTimeout) {
                          clearTimeout(timeoutTimer);
                          clearInterval(timer);
                          this.setState({
                            checking: false,
                            showCheckResultDialog: true,
                            inProtect,
                            controlDuplicateApplications,
                            duplicateWhenUpload: false,
                          });
                          return;
                        }
                        doing = false;
                      },
                    });
                  };
                  getResult();
                  timer = setInterval(getResult, 1000);
                }
              },
            });
          }
        },
      });
    } else {
      this.setState({ selfCheckError: i18n.t`排查前请填写姓名+手机+邮箱` });
    }
  };

  // 获取当前org下配置的猎头必填字段信息
  getHeadhunterRequiredFields = () => {
    const orgId = this.props.match.params.orgId;
    return this.props.orgEntities?.[orgId]?.headhunterRequiredFields ?? {};
  };

  getIsRequire = (key) => {
    const { mandatoryFields } = this.state;
    if (mandatoryFields.includes(key)) {
      return true;
    }
    const headhunterRequiredFields = this.getHeadhunterRequiredFields();
    return !!headhunterRequiredFields[key]?.require;
  };

  getIsNotNull = (key) => {
    const { mandatoryFields } = this.state;
    if (mandatoryFields.includes(key)) {
      return true;
    }
    const headhunterRequiredFields = this.getHeadhunterRequiredFields();
    return !!headhunterRequiredFields[key]?.notNull;
  };

  getNavs() {
    const { formError, mandatoryFields } = this.state;
    const headhunterRequiredFields = this.getHeadhunterRequiredFields();

    let requireFields = Object.keys(headhunterRequiredFields).filter(
      (key) => mandatoryFields.includes(key) || !!headhunterRequiredFields[key]?.require
    );
    // resume 默认必填
    requireFields = ['resume', ...requireFields];

    const navs = ['uploadInfo', 'basicInfo']
      .filter((type) => {
        const feilds = CATEGORIES[type];
        return feilds.some((key) => requireFields.indexOf(key) > -1);
      })
      .map((type) => {
        const feilds = CATEGORIES[type];
        const hasError = feilds.some((key) => formError[key]);
        return {
          title: CATEGORIES_TITLE[type],
          selector: `[data-nav-id=${type}]`,
          hasError,
        };
      });

    return navs;
  }

  renderMainTab() {
    const {
      tempResume,
      orgEntities,
      match: {
        params: { orgId },
      },
    } = this.props;
    const currentOrg = orgEntities[orgId] || {};
    const {
      formData,
      formError,
      fileList,
      showHint,
      submitting,
      checking,
      showCheckResultDialog,
      selfCheckError,
      isShowWaitModal,
      mandatoryFields,
      showPrivacy,
      policyList,
      isPrivacyAgreed,
    } = this.state;
    const {
      headhunterRequiredFields = {},
      headhunterSelfCheckDup,
      disabledUploadFileTypes = [],
    } = currentOrg;

    const plainInputs = PLAIN_INPUT.filter(
      (type) => mandatoryFields.includes(type) || !!headhunterRequiredFields[type]?.require
    );
    const navs = this.getNavs();

    return (
      <div className="candidate-info__content">
        <div className="candidate-info-form" data-nav-id="uploadInfo">
          <div className="candidate-info-form-title">
            <span>{i18n.t`上传`}</span>
          </div>
          <div className="candidate-info-form-content">
            <div className="candidate-info-form-item candidate-info-form-resume">
              <FormItem title={i18n.t`简历`} isRequired={true} errorMsg={formError.resume}>
                <Upload
                  accept={filterDisabledFile(ACCEPTED_RESUME_MIMETYPE, disabledUploadFileTypes)}
                  triggerClassName={styles.uploadBtn}
                  disabled={tempResume.loading}
                  stretched
                  buttonType="tertiaryLight"
                  uploadText={i18n.t`上传简历`}
                  onUploadChange={this.onUploadChange}
                />
                <div className="form-item__file-util">
                  <span>{RESUME_ACCEPTABLE_NOTICE}</span>
                  <br />
                  {tempResume.loading && (
                    <div className="form-item__file-util-loading">
                      <LoadingIndicator type="upload" percent={tempResume.percent} />
                      {i18n.t`正在上传和解析简历`}
                    </div>
                  )}
                  <span className="link file-name">{tempResume.resumeName}</span>
                </div>
              </FormItem>
            </div>
            {this.getIsRequire('attachments') && (
              <div className="candidate-info-form-item">
                <FormItem
                  title={i18n.t`附件`}
                  isRequired={this.getIsNotNull('attachments')}
                  errorMsg={formError.attachments}
                >
                  <Upload
                    accept={filterDisabledFile(
                      ATTACHMENT_ACCEPTABLE_TYPES,
                      disabledUploadFileTypes
                    )}
                    multiple
                    triggerClassName={styles.uploadBtn}
                    stretched
                    buttonType="tertiaryLight"
                    uploadText={fileList.length === 0 ? i18n.t`上传附件` : i18n.t`继续上传附件`}
                    onUploadChange={this.onAttachmentUploadChange}
                  />

                  <div className="form-item__file-util">{i18n.t`支持文档、图片、压缩包、视频、音频、设计文件等格式文件，所有附件大小总和不超过50MB`}</div>

                  <ol className="form-item__attachment-list">
                    {fileList.map((attachment) => (
                      <li key={attachment.uid} className="form-item__attachment-list-item">
                        <div className="attachment-file">
                          <span className="file-name" title={attachment.name}>
                            {attachment.name}
                          </span>
                          （{getReadableFileSize(attachment.size)}）
                        </div>
                        <div className="file-util">
                          {attachment.status === Upload.fileStatus.success ? (
                            <span className="success">{i18n.t`上传成功`}</span>
                          ) : (
                            attachment.status === Upload.fileStatus.uploading && (
                              <LoadingIndicator type="upload" percent={attachment.percent} />
                            )
                          )}
                          <Button
                            type="textDanger"
                            onClick={() => this.onDelete(attachment)}
                          >{i18n.t`删除`}</Button>
                        </div>
                      </li>
                    ))}
                  </ol>
                </FormItem>
              </div>
            )}
          </div>
        </div>
        <div className="candidate-info-form candidate-personal-info-form" data-nav-id="basicInfo">
          {plainInputs.length > 0 && (
            <div className="candidate-info-form-title">
              <span>{i18n.t`候选人信息`}</span>
            </div>
          )}
          {plainInputs.length > 0 && !!showHint && (
            <div className="candidate-info-hint">
              <Icon name="warning" className="candidate-info-hint-icon" />
              {i18n.t`请核对候选人信息是否正确，以防影响简历查重结果`}
            </div>
          )}
          <div className="candidate-info-form-content">
            {plainInputs.map((type) => (
              <FormItem
                key={type}
                type={type}
                defaultValue={formData[type]}
                mandatoryFields={mandatoryFields}
                secondDefaultValue={type === 'citizenId' ? formData.certificateType : null}
                isRequired={this.getIsNotNull(type)}
                onChange={this.createFormOnChangeHandler(type)}
                onBlur={this.validateForm}
                errorMsg={formError[type]}
              />
            ))}
          </div>
          {showPrivacy && (
            <div className={cms.pdy16}>
              <PcPolicyChecker
                isAgree={isPrivacyAgreed}
                onChange={(isAgree) => {
                  this.setState({ isPrivacyAgreed: isAgree });
                }}
                protocolContentRespDTOList={policyList}
              />
            </div>
          )}
          {!!selfCheckError && <div className="error-message">{selfCheckError}</div>}
          <div className="candidate-info-form__actions">
            {!!headhunterSelfCheckDup && (
              <Button
                className="candidate-info-form__check-btn"
                type="secondary"
                isLoading={checking}
                disabled={checking}
                onClick={this.onCheck}
              >
                {checking ? i18n.t`正在排查中` : i18n.t`排查重复候选人`}
              </Button>
            )}
            <Button
              className="candidate-info-form__submit"
              onClick={this.formOnSubmit}
              disabled={submitting}
              isLoading={submitting}
            >
              {i18n.t`提交候选人`}
            </Button>
          </div>
        </div>
        <FormNav
          navs={navs}
          formSelector=".candidate-info__content"
          useBodyScrollTop={true}
          offsetTop={150}
          fixTop={182}
        />
        <Dialog
          title={i18n.t`排查结果`}
          submitText={i18n.t`我知道了`}
          size="sm"
          needCancelButton={false}
          onSubmit={this.onCloseCheckResultDialog}
          onCancel={this.onCloseCheckResultDialog}
          isOpen={showCheckResultDialog}
        >
          {this.renderSelfCheckResult()}
        </Dialog>
        <Modal
          isOpen={isShowWaitModal}
          size="sm"
          title={i18n.t`提示`}
          canCloseOnEsc={false}
          canCloseOnClickMask={false}
          onClose={this.onCloseWaitModal}
          canClose={false}
        >
          <p>{i18n.t`文件上传中，请等待`}</p>
        </Modal>
      </div>
    );
  }

  renderSelfCheckResult() {
    const { inProtect, controlDuplicateApplications, duplicateWhenUpload } = this.state;
    if (inProtect || controlDuplicateApplications) {
      return (
        <div className="candidate-info-form__dup-result">{i18n.t`系统中存在重复候选人，且在保护期内，不可推荐`}</div>
      );
    } else if (duplicateWhenUpload) {
      return (
        <div className="candidate-info-form__dup-result">{i18n.t`系统中存在重复候选人，但不在保护期内，可推荐`}</div>
      );
    }
    return <div>{i18n.t`系统中无重复候选人，可推荐`}</div>;
  }

  uploadExcelFile = (file) => {
    if (this.state.excelUploading) {
      return;
    }
    const {
      match: {
        params: { orgId, jobId },
      },
    } = this.props;
    this.setState({
      excelUrl: '',
      excelKey: '',
      excelName: file.name,
      excelRows: [],
      excelErrorMessage: '',
      excelUploading: true,
      status: 'uploading',
    });
    const start = Date.now();

    mokaRequest('/api/outer/ats-candidate/candidate/headhunters/uploadExcel')
      .attach({
        files: [
          {
            field: 'excelFile',
            file: file,
            option: file.name,
          },
        ],
        data: {
          orgId,
          jobId,
          contractId: this.getContractId(),
        },
        onProgress: (e) => {
          if (e.percent) {
            const surplus = Math.ceil(
              ((Date.now() - start) / 1000) * ((100 - e.percent) / e.percent)
            );
            this.setState({ percent: Math.ceil(e.percent), surplus });
          }
        },
      })
      .then((data) => {
        this.setState({
          ...data,
          excelUploading: false,
          excelName: file.name,
          percent: 0,
          surplus: 0,
        });
        // 解析上传成功
        this.setState({
          status: 'success',
        });
        // 有候选人触发了保护期
        if (data?.protectedCandidatesCount > 0) {
          this.setState({
            excelErrorMessage: i18n.t`其中有${data?.protectedCandidatesCount}位候选人处于保护期，保护期已标记在Excel表格最后一列，点击下载查看`,
          });
        }
      })
      .catch((error) => {
        this.setState({
          excelErrorMessage:
            i18n.t`上传、处理Excel文件的时候发生错误：` + error.message || i18n.t`请重试。`,
          excelUploading: false,
          status: 'error',
          excelUrl: error.response?.body?.data?.excelUrl,
        });
      });
  };

  showUploadExcelDialog = () => {
    if (this.$excelFileInput && this.state.status !== 'uploading') {
      this.$excelFileInput.value = null;
      this.$excelFileInput.click();
    }
  };

  downloadExcelFileForCorrection = () => downloadByUrl(this.state.excelUrl);

  hideExcelDownload = () => {
    // 后端没有删除的API，掩耳盗铃
    if (this.$excelFileInput) {
      this.$excelFileInput.value = null;
    }
    this.setState({
      excelUrl: '',
      excelErrorMessage: '',
      status: '',
      excelName: '',
      excelRows: [],
    });
  };

  submitExcel = () => {
    const {
      match: {
        params: { orgId, jobId },
      },
      location: { pathname },
    } = this.props;

    const { excelRows, excelKey, excelName, excelSubmitting } = this.state;
    if (excelSubmitting) {
      return;
    }
    this.setState({ excelSubmitting: true });
    const callback = (error) => {
      this.setState({ excelSubmitting: false });
      if (error) {
        this.props.errorToast(error);
      } else {
        this.props.history.push(`${pathname}/excel_result`);
        this.props.fetchExcelApplicationStats(orgId);
        this.props.fetchJobList(orgId);
      }
    };
    const data = {
      candidates: excelRows,
      jobId,
      excelKey,
      excelName,
      rpo: this.isRpoRecommend(),
      contractId: this.getContractId(),
    };
    this.props.batchUploadCandidates(orgId, data, callback);
  };

  refExcelFileInput = ($excelFileInput) => (this.$excelFileInput = $excelFileInput);

  refDropZone = (el) => (this.$dropZone = el);

  onCloseWaitModal = () => this.setState({ isShowWaitModal: false });

  onDragEnter = (event) => {
    event.preventDefault();
    if (this.dragTargets.indexOf(event.target) === -1) {
      this.dragTargets.push(event.target);
    }
    this.setState({ dropZoneActive: true });
  };

  onDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  onDragLeave = (event) => {
    event.preventDefault();
    this.dragTargets = this.dragTargets.filter(
      (el) => el !== event.target && this.$dropZone.contains(el)
    );
    if (this.dragTargets.length > 0) {
      return;
    }
    this.setState({ dropZoneActive: false });
  };

  onFileChange = () => {
    if (this.$excelFileInput) {
      const file = this.$excelFileInput.files[0];
      this.uploadExcelFile(file);
    }
  };

  onDrop = (event) => {
    event.preventDefault();
    this.setState({ dropZoneActive: false });
    if (this.state.status === 'uploading') {
      return;
    }
    const dt = event.dataTransfer;
    if (dt && dt.files && dt.files.length) {
      const file = dt.files[0];
      if (!file.name || !file.name.endsWith('.xlsx')) {
        this.setState({
          excelName: file.name,
          excelErrorMessage: i18n.t`请上传xlsx格式的文件`,
          status: 'error',
          excelUrl: '',
          excelRows: [],
        });
      } else {
        this.uploadExcelFile(file);
      }
    }
  };

  renderExcelTab(orgId, hireMode) {
    const {
      excelName,
      excelUrl,
      excelErrorMessage,
      excelKey,
      excelRows,
      excelSubmitting,
      dropZoneActive,
      percent,
      surplus,
      status,
      showPrivacy,
      policyList,
      isPrivacyAgreed,
    } = this.state;

    const submitDisabled =
      !excelRows.length ||
      !excelKey ||
      excelErrorMessage ||
      excelSubmitting ||
      (showPrivacy && !isPrivacyAgreed);

    return (
      <div className="candidate-info__content">
        <div className="candidate-info-form" data-nav-id="uploadExcel">
          <div className="candidate-info-form-title">
            <span>{i18n.t`上传`}</span>
          </div>
          <div className="excel-section">
            <ExcelUploader
              dropZoneActive={dropZoneActive}
              hireMode={hireMode}
              orgId={orgId}
              excelUrl={excelUrl}
              excelErrorMessage={excelErrorMessage}
              excelRows={excelRows}
              excelSubmitting={excelSubmitting}
              excelName={excelName}
              percent={percent}
              surplus={surplus}
              status={status}
              refDropZone={this.refDropZone}
              onDragEnter={this.onDragEnter}
              onDragOver={this.onDragOver}
              onDragLeave={this.onDragLeave}
              onDrop={this.onDrop}
              onFileChange={this.onFileChange}
              refExcelFileInput={this.refExcelFileInput}
              downloadExcelFileForCorrection={this.downloadExcelFileForCorrection}
              hideExcelDownload={this.hideExcelDownload}
              showUploadExcelDialog={this.showUploadExcelDialog}
            />
          </div>
          {showPrivacy && (
            <div className={cms.pdy16}>
              <PcPolicyChecker
                isAgree={isPrivacyAgreed}
                onChange={(isAgree) => {
                  this.setState({ isPrivacyAgreed: isAgree });
                }}
                protocolContentRespDTOList={policyList}
              />
            </div>
          )}
          <div className="excel-section__xlsx-submit">
            <Button
              type="positive"
              isLoading={excelSubmitting}
              disabled={submitDisabled}
              onClick={this.submitExcel}
            >{i18n.t`提交应聘申请`}</Button>
          </div>
        </div>
      </div>
    );
  }

  renderTabSwitcher() {
    const { activeTabId } = this.state;
    const tabs = [
      { id: 'main', label: i18n.t`基本上传`, active: activeTabId === 'main' || !activeTabId },
      { id: 'excel', label: i18n.t`Excel上传`, active: activeTabId === 'excel' },
    ];

    const renderedTabs = tabs.map((tab) => {
      const onClick = () => {
        this.setState({ activeTabId: tab.id });
      };
      return (
        <div
          key={tab.id}
          className={`candidate__tab_switch__tab ${tab.active ? 'active' : ''}`}
          onClick={onClick}
        >
          {tab.label}
        </div>
      );
    });

    return <div className="candidate__tab_switch">{renderedTabs}</div>;
  }

  render() {
    const {
      jobEntities,
      orgEntities,
      route,
      match: {
        params: { orgId, jobId },
      },
      location,
    } = this.props;
    const { activeTabId, mandatoryFields, isConfirmResumeAuthorization, isConfirmResumeModalOpen } =
      this.state;

    const currentOrg = orgEntities[orgId] || {};
    const currentJob = jobEntities[jobId] || {};
    const { hireMode } = currentJob;

    const contractId = this.getContractId();

    const prevPath = {
      name: i18n.t`职位详情`,
      url: `/headhunters/${currentOrg.id}/job/${jobId}/${contractId}`,
    };
    const breadcrumbArray = [
      {
        name: i18n.t`首页`,
        url: '/headhunters/',
      },
      {
        name: currentOrg.name,
        url: `/headhunters/${currentOrg.id}/org/jobs`,
      },
      prevPath,
      {
        name: i18n.t`推荐候选人`,
      },
    ];

    const companyName = currentOrg.name;

    // 是否匹配子路由
    const branch = matchRoutes(route.routes, location.pathname);
    const isShowRpoContent = this.isShowRpoContent();
    const headhunterTypeHint = isShowRpoContent ? i18n.t`当前为RPO推荐` : i18n.t`当前为猎头推荐`;

    return (
      <div>
        <div className="candidate__header-wrapper">
          <div className="candidate__header">
            <Breadcrumb paths={breadcrumbArray} />
          </div>
        </div>
        {branch.length && route.routes ? (
          renderRoutes(route.routes)
        ) : (
          <div className="panel candidate-info">
            <Alert className={styles.alert} type="info" message={headhunterTypeHint} />
            {!!mandatoryFields?.length && (
              <Alert
                className={classNames(styles.alert, cms.mgt4)}
                type="info"
                message={i18n.t`该公司已开启非候选人主动投递需候选人确认的功能，提交后，你将无法修改简历，请仔细核对后提交`}
              />
            )}
            <div className="panel__header candidate-info__header">
              <JobHeader
                job={currentJob}
                companyName={companyName}
                headhunterJobFields={currentOrg.headhunterJobFields || {}}
              />
            </div>
            {isShowRpoContent && this.renderTabSwitcher()}
            <div
              className={`candidate__tab ${!activeTabId || activeTabId === 'main' ? 'active' : ''}`}
            >
              {(!activeTabId || activeTabId === 'main') && this.renderMainTab()}
            </div>
            {this.isShowRpoContent() && (
              <div className={`candidate__tab ${activeTabId === 'excel' ? 'active' : ''}`}>
                {this.renderExcelTab(orgId, hireMode)}
              </div>
            )}
            <UploadResumePromptModal
              isOpen={isConfirmResumeModalOpen}
              isConfirmChecked={isConfirmResumeAuthorization}
              currentOrg={currentOrg}
              onCheckChange={(checked) => {
                this.setState({ isConfirmResumeAuthorization: checked });
              }}
              onClose={() => {
                this.setState({
                  isConfirmResumeModalOpen: false,
                  isConfirmResumeAuthorization: false,
                });
              }}
              onConfirm={this.formOnSubmitFun}
            />
          </div>
        )}
      </div>
    );
  }
}

CandidateInfo.propTypes = {
  orgEntities: PropTypes.object,
  jobEntities: PropTypes.object,
  tempResume: PropTypes.object,
  fetchJobList: PropTypes.func,
};

export default CandidateInfo;
